
import { Component, Vue, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";

// Types
import { Domain, DomainGroup } from "@/store/user-management/types";
import { UserGroupTable, UserPermissions, VueBootstrapField } from "@/store/types";

// Enums
import { ModalMode } from "@/store/user-management/enums";

// Icon
import SureViewIcon from "@/components/SureViewIcon.vue";

// Modals
import DomainGroupMappingModal from "./DomainGroupMappingModal.vue";
import DeleteDomainGroupMappingModal from "./DeleteDomainGroupMappingModal.vue";

// API
import api from "@/services/api.service";

// Third Party
import VuePerfectScrollbar from "vue-perfect-scrollbar";

// Namespaces
const UserManagement = namespace("userManagement");

export const domainGroupTemplate = {
    domainId: 0,
    domainGroupName: "",
    userGroupId: 0,
    userGroupTitle: ""
};

@Component({
    components: {
        "sureview-icon": SureViewIcon,
        "vue-perfect-scrollbar": VuePerfectScrollbar,
        "domain-group-mapping-modal": DomainGroupMappingModal,
        "delete-domain-group-mapping-modal": DeleteDomainGroupMappingModal
    }
})

export default class DomainGroupMappingTable extends Vue {
    // Props
    @Prop({ default: null, type: Number }) domainId: number;

    // Getters
    @UserManagement.Getter("getDomainGroups") allDomainGroups: DomainGroup[];
    @UserManagement.Getter private getDomain: (domainId: number) => Domain;
    @Getter getPermissions: UserPermissions;

    // Actions
    @UserManagement.Action loadDomainGroups: () => Promise<void>;

    // Domain Group
    private domainGroupMappingTableLoading: boolean = true;
    private domainGroupMappings: DomainGroup[] = [];
    private domainGroupModal: boolean = false;
    private deleteDomainGroupModal: boolean = false;
    private selectDomainGroup: DomainGroup = null;
    private filterSearchDomainGroup: string = "";
    private defaultDomainGroup: DomainGroup = domainGroupTemplate;

    // User Group List
    private userGroups: UserGroupTable[] = [];
    private selectableUserGroups: UserGroupTable[] = [];

    // Modal modes
    private modes: typeof ModalMode = ModalMode;
    private modalMode: ModalMode = ModalMode.View;

    // Table's fields
    private fields: VueBootstrapField[] = [
        { key: "userGroupTitle", label: "User Group", sortable: true },
        { key: "domainGroupName", label: "Domain Group", sortable: true },
        { key: "actions", label: "Actions", sortable: false }
    ];

    public async mounted() {
        if (this.isDomainGroupMappingEnabled) {
            // Loads all existing domain group mapping
            await this.loadDomainGroups();

            // Loads all existing user groups
            this.userGroups = await api.getUserGroup();
        }
        this.domainGroupMappingTableLoading = false;
    }

    // Filters all existing domain group mapping by domain Id - used for the table's data
    private get domainGroupMappingsByDomainId(): DomainGroup[] {
        return this.allDomainGroups.filter( dg => dg.domainId === this.domainId);
    }

    // If permission is on / off
    public get isDomainGroupMappingEnabled(): boolean {
        return this.getPermissions.canEditDomainGroups || this.getPermissions.canViewDomainGroups;
    }

    public get canEditDomainGroups(): boolean {
        return this.getPermissions.canEditDomainGroups;	
    }

    public get showInvalidDataAlert(): boolean {
        return this.domainGroupMappingsByDomainId.some(dg => dg.userGroupTitle == "User Group Not Found");
    }

    // Sets the necessary props for the modal
    private setSelectedDomainGroup(domainGroup: DomainGroup, mode: ModalMode): void {
        if (mode === ModalMode.Delete) {
            this.deleteDomainGroupModal = !this.deleteDomainGroupModal;
        } else {
            if (mode === ModalMode.Create) {
                domainGroup.domainId = this.domainId;
            }
            this.domainGroupModal = !this.domainGroupModal;
        }

        this.selectDomainGroup = domainGroup;
        this.modalMode = mode;
        this.setSelectableUserGroups(domainGroup);
    }

    // Removes all of the user groups that are already mapped
    private setSelectableUserGroups(domainGroup: DomainGroup): void {
        this.selectableUserGroups = this.userGroups.filter( ug => {
            // Keep user group if selected domain group had it selected
            if (ug.userGroupId === domainGroup.userGroupId) return true;
            
            return !this.allDomainGroups.some( dg => {
                return dg.userGroupId === ug.userGroupId;
            });
        });
    }

    private viewDomainGroup(domainGroup: DomainGroup): void {
        this.domainGroupModal = !this.domainGroupModal;
        this.selectDomainGroup = domainGroup;
        this.modalMode = ModalMode.View;
        this.setSelectableUserGroups(domainGroup);
    }
}
